import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Building2, Mail, Phone, User, Loader2, Check, Plus, Inbox, X,
    ArrowRight, ShieldCheck, Copy,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { adminProvidersApi } from "@/lib/api/adminProviders";
import { providerTypesApi } from "@/lib/api/providerTypes";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const KYC_BADGES = {
    DRAFT:    { en: "Draft",    ar: "مسودة",    cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
    PENDING:  { en: "In review",ar: "قيد المراجعة", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    APPROVED: { en: "Approved", ar: "معتمد",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    REJECTED: { en: "Rejected", ar: "مرفوض",   cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const EMPTY_FORM = {
    corporate_name: "", type: "CLINIC",
    primary_contact_name: "", admin_email: "", phone: "",
};

// ── Add provider modal ──────────────────────────────────────────────────────
function AddProviderModal({ open, onClose, onCreated, providerTypes }) {
    const { lang } = useI18n();
    const tr = (en, ar) => lang === "ar" ? ar : en;

    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [created, setCreated] = useState(null);

    useEffect(() => {
        if (open) { setForm(EMPTY_FORM); setError(""); setCreated(null); }
    }, [open]);

    if (!open) return null;

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSubmitting(true);
        try {
            const payload = {
                corporate_name: form.corporate_name.trim(),
                type: form.type,
            };
            if (form.admin_email.trim()) {
                payload.admin_email = form.admin_email.trim().toLowerCase();
                payload.admin_full_name = form.primary_contact_name.trim() || form.corporate_name.trim();
            } else if (form.primary_contact_name.trim()) {
                payload.primary_contact_name = form.primary_contact_name.trim();
            }
            if (form.phone.trim()) payload.primary_phone = form.phone.trim();

            const res = await adminProvidersApi.create(payload);
            setCreated(res);
            onCreated();
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="kiosk-add-provider-modal"
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <h2 className="text-base font-semibold">
                        {created ? tr("Provider added", "تم إضافة المزود") : tr("Add a provider", "إضافة مزود")}
                    </h2>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded-lg hover:bg-secondary">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {created ? (
                    <div className="p-5 space-y-4">
                        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-4">
                            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full mb-2">
                                <Check className="h-3 w-3" />
                                {tr("Created", "تم الإنشاء")}
                            </div>
                            <p className="text-base font-semibold text-foreground">{created.corporate_name}</p>
                            <p className="text-xs text-muted-foreground">{created.type}</p>
                            {created.admin_temporary_password && (
                                <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                                    <p className="text-[11px] uppercase tracking-wide text-amber-700 font-bold">
                                        {tr("Temporary password for provider admin", "كلمة مرور مؤقتة لمسؤول المزود")}
                                    </p>
                                    <div className="mt-1 flex items-center gap-2">
                                        <code className="font-mono font-semibold text-amber-900 text-sm">
                                            {created.admin_temporary_password}
                                        </code>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(created.admin_temporary_password);
                                                toast.success(tr("Copied", "تم النسخ"));
                                            }}
                                            className="ms-auto inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900"
                                        >
                                            <Copy className="h-3 w-3" /> {tr("Copy", "نسخ")}
                                        </button>
                                    </div>
                                </div>
                            )}
                            <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                                {tr(
                                    "Submitted for KYC review. The provider can log in and complete their profile, then ops will approve.",
                                    "تم الإرسال لمراجعة KYC. يمكن للمزود تسجيل الدخول واستكمال ملفه ثم سيراجع فريق العمليات."
                                )}
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => { setCreated(null); }}
                                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
                            >
                                <Plus className="h-4 w-4" /> {tr("Add another", "إضافة آخر")}
                            </button>
                            <button
                                onClick={onClose}
                                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-secondary text-foreground text-sm font-semibold hover:bg-secondary/80"
                            >
                                {tr("Done", "تم")}
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-5 space-y-3.5">
                        {/* Corporate name */}
                        <div>
                            <label className="block text-sm font-medium mb-1.5">
                                {tr("Provider name", "اسم المزود")}
                            </label>
                            <div className="relative">
                                <Building2 className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <input
                                    required
                                    autoFocus
                                    value={form.corporate_name}
                                    onChange={(e) => update({ corporate_name: e.target.value })}
                                    placeholder={tr("e.g. Cairo Heart Clinic", "مثال: عيادة القلب بالقاهرة")}
                                    className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                    data-testid="kiosk-prov-name"
                                />
                            </div>
                        </div>

                        {/* Type */}
                        <div>
                            <label className="block text-sm font-medium mb-1.5">{tr("Type", "النوع")}</label>
                            <select
                                value={form.type}
                                onChange={(e) => update({ type: e.target.value })}
                                className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                data-testid="kiosk-prov-type"
                            >
                                {(providerTypes || []).map((t) => (
                                    <option key={t.code} value={t.code}>
                                        {lang === "ar" ? t.name_ar : t.name_en}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Contact name */}
                        <div>
                            <label className="block text-sm font-medium mb-1.5">
                                {tr("Contact person", "الشخص المسؤول")}
                            </label>
                            <div className="relative">
                                <User className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <input
                                    value={form.primary_contact_name}
                                    onChange={(e) => update({ primary_contact_name: e.target.value })}
                                    placeholder={tr("Owner / manager name", "اسم المالك / المدير")}
                                    className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                    data-testid="kiosk-prov-contact"
                                />
                            </div>
                        </div>

                        {/* Admin email */}
                        <div>
                            <label className="block text-sm font-medium mb-1.5">
                                {tr("Admin email", "بريد المسؤول")}
                                <span className="ms-1.5 text-xs text-muted-foreground font-normal">
                                    ({tr("creates a login for them", "يُنشئ حساب دخول لهم")})
                                </span>
                            </label>
                            <div className="relative">
                                <Mail className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <input
                                    type="email"
                                    dir="ltr"
                                    value={form.admin_email}
                                    onChange={(e) => update({ admin_email: e.target.value })}
                                    placeholder="manager@clinic.com"
                                    className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                    data-testid="kiosk-prov-email"
                                />
                            </div>
                        </div>

                        {/* Phone */}
                        <div>
                            <label className="block text-sm font-medium mb-1.5">
                                {tr("Phone", "الهاتف")}
                                <span className="ms-1.5 text-xs text-muted-foreground font-normal">
                                    ({tr("optional", "اختياري")})
                                </span>
                            </label>
                            <div className="relative">
                                <Phone className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <input
                                    type="tel"
                                    dir="ltr"
                                    value={form.phone}
                                    onChange={(e) => update({ phone: e.target.value })}
                                    placeholder="+20…"
                                    className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                    data-testid="kiosk-prov-phone"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
                                {error}
                            </div>
                        )}

                        <div className="rounded-xl border border-border/50 bg-muted/30 p-3 flex items-start gap-2.5">
                            <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                            <p className="text-xs text-muted-foreground leading-relaxed">
                                {tr(
                                    "The provider will be created in DRAFT status and submitted to ops for KYC review. You'll be credited as the referrer.",
                                    "سيُنشأ المزود في حالة مسودة وسيُرسل لفريق العمليات لمراجعة KYC. ستُنسب الإحالة إليك."
                                )}
                            </p>
                        </div>

                        <div className="flex gap-2 pt-1">
                            <button
                                type="button"
                                onClick={onClose}
                                className="inline-flex items-center justify-center h-10 px-4 rounded-xl bg-secondary text-foreground text-sm font-semibold hover:bg-secondary/80"
                            >
                                {tr("Cancel", "إلغاء")}
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                                data-testid="kiosk-prov-submit"
                            >
                                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                {tr("Add provider", "إضافة المزود")}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function KioskProvidersPage() {
    const { lang } = useI18n();
    const tr = (en, ar) => lang === "ar" ? ar : en;

    const [providers, setProviders] = useState(null);
    const [providerTypes, setProviderTypes] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await adminProvidersApi.list({ limit: 100 });
            setProviders(res.items || []);
            setTotal(res.total || 0);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
            setProviders([]);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => {
        providerTypesApi.listPublic()
            .then((items) => setProviderTypes(items || []))
            .catch(() => setProviderTypes([]));
    }, []);

    return (
        <div className="max-w-4xl space-y-6 fade-up" data-testid="page-kiosk-providers">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-sm font-medium text-primary">{tr("Sales Kiosk", "كشك المبيعات")}</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight">
                        {tr("My providers", "مزودي")}
                    </h1>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                        {tr(
                            "Providers you've onboarded to the platform.",
                            "المزودون الذين أضفتهم إلى المنصة."
                        )}
                    </p>
                </div>
                <button
                    onClick={() => setOpen(true)}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
                    data-testid="kiosk-add-provider-btn"
                >
                    <Plus className="h-4 w-4" />
                    {tr("Add provider", "إضافة مزود")}
                </button>
            </header>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            ) : providers.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-card p-12 flex flex-col items-center gap-3 text-center">
                    <Inbox className="h-10 w-10 text-muted-foreground" />
                    <div>
                        <p className="text-sm font-medium text-foreground">
                            {tr("No providers yet", "لم تُضِف أي مزود بعد")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {tr(
                                "Add a clinic, pharmacy or hospital to start earning.",
                                "أضف عيادة أو صيدلية أو مستشفى لبدء جني العمولة."
                            )}
                        </p>
                    </div>
                    <button
                        onClick={() => setOpen(true)}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
                    >
                        <Plus className="h-3.5 w-3.5" />
                        {tr("Add your first provider", "أضف أول مزود")}
                    </button>
                </div>
            ) : (
                <>
                    <div className="text-xs text-muted-foreground">
                        {tr(`Showing ${providers.length} of ${total}`, `عرض ${providers.length} من ${total}`)}
                    </div>
                    <div className="space-y-3">
                        {providers.map((p) => {
                            const kyc = KYC_BADGES[p.kyc_status] || KYC_BADGES.DRAFT;
                            const ptype = providerTypes.find((t) => t.code === p.type);
                            return (
                                <div
                                    key={p.id}
                                    className="rounded-2xl border border-border bg-card p-4 flex items-start gap-4"
                                    data-testid={`kiosk-prov-row-${p.id}`}
                                >
                                    <div className="h-12 w-12 rounded-xl bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
                                        {p.logo_url ? (
                                            <img src={p.logo_url} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <Building2 className="h-6 w-6 text-primary/40" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <h3 className="text-base font-semibold truncate">{p.corporate_name}</h3>
                                            <span className={`shrink-0 inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${kyc.cls}`}>
                                                {lang === "ar" ? kyc.ar : kyc.en}
                                            </span>
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {ptype ? (lang === "ar" ? ptype.name_ar : ptype.name_en) : p.type}
                                            {p.branch_count > 0 && (
                                                <>
                                                    <span className="mx-1.5">•</span>
                                                    {tr(`${p.branch_count} branches`, `${p.branch_count} فروع`)}
                                                </>
                                            )}
                                        </p>
                                        {p.admin?.email && (
                                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                                {p.admin.email}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            <div className="pt-2">
                <Link
                    to="/kiosk/sales"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                >
                    {tr("View my sales overview", "عرض ملخص مبيعاتي")}
                    <ArrowRight className="h-3.5 w-3.5" />
                </Link>
            </div>

            <AddProviderModal open={open} onClose={() => setOpen(false)} onCreated={load} providerTypes={providerTypes} />
        </div>
    );
}
